import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import CommercialHeader from "@/components/commercial/CommercialHeader";
import { api, fmtEur, fmtDateTime } from "@/lib/commercialApi";
import { stageById } from "@/lib/commercialStages";
import {
  Loader2, Phone, PhoneCall, FileCheck2, CalendarClock, AlertCircle, Sparkles,
  TrendingUp, Target, FileText, Wallet, Calendar, ChevronRight,
} from "lucide-react";

const isSameDay = (iso) => {
  if (!iso) return false;
  const d = new Date(iso); const n = new Date();
  return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth() && d.getDate() === n.getDate();
};
const isPast = (iso) => iso && new Date(iso) < new Date();
const isFuture = (iso) => iso && new Date(iso) >= new Date();

export default function ComDashboard() {
  const [opps, setOpps] = useState(null);
  const [appts, setAppts] = useState(null);
  const [kpis, setKpis] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { load(); }, []);
  async function load() {
    const [a, b, c] = await Promise.allSettled([
      api.get("/opportunities", { params: { limit: 500 } }),
      api.get("/appointments", { params: { limit: 500 } }),
      api.get("/dashboard"),
    ]);
    setOpps(a.status === "fulfilled" ? a.value.data.opportunities : []);
    setAppts(b.status === "fulfilled" ? b.value.data.appointments : []);
    setKpis(c.status === "fulfilled" ? c.value.data.kpis : {});
  }

  const buckets = useMemo(() => {
    if (!opps || !appts) return null;
    const open = opps.filter((o) => !["signe", "perdu", "abandonne"].includes(o.stage));
    return {
      rappelsJour: open.filter((o) => o.date_prochaine_action && isSameDay(o.date_prochaine_action)),
      rappelsEnRetard: open.filter((o) => o.date_prochaine_action && isPast(o.date_prochaine_action) && !isSameDay(o.date_prochaine_action)),
      rdvJour: appts.filter((a) => isSameDay(a.scheduled_at) && !["annule", "absent"].includes(a.status)),
      rdvAConfirmer: appts.filter((a) => a.status === "programme" && isFuture(a.scheduled_at)),
      sansAction: open.filter((o) => !o.prochaine_action && !o.date_prochaine_action && o.stage === "interesse"),
      incompletes: open.filter((o) => missingFields(o).length >= 2 && o.source !== "google_import_orphan"),
      sansAttribution: open.filter((o) => !o.teleprospectrice || !o.commercial),
      recentes: [...opps].sort((x, y) => (y.created_at || "").localeCompare(x.created_at || "")).slice(0, 5),
    };
  }, [opps, appts]);

  if (!buckets) {
    return (
      <div className="min-h-screen bg-brand-paper">
        <CommercialHeader />
        <div className="max-w-[1400px] mx-auto px-4 lg:px-10 py-10">
          <div className="flex items-center gap-2 text-brand-subtle"><Loader2 className="h-4 w-4 animate-spin" /> Chargement…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-paper">
      <CommercialHeader />
      <div className="max-w-[1400px] mx-auto px-4 lg:px-10 py-6 lg:py-10 space-y-6">
        <div>
          <div className="eyebrow">Suivi commercial</div>
          <h1 className="font-display text-3xl sm:text-4xl text-brand-ink mt-1">À traiter aujourd'hui</h1>
          <p className="mt-2 text-sm text-brand-subtle max-w-[60ch]">
            Les actions prioritaires de la journée. Un clic suffit pour appeler, confirmer un RDV ou compléter une fiche.
          </p>
        </div>

        {/* Rappels du jour */}
        <Section
          title="Rappels du jour"
          Icon={PhoneCall}
          count={buckets.rappelsJour.length}
          emptyText="Pas de rappel prévu aujourd'hui. Bonne journée."
          testid="section-rappels-jour"
        >
          {buckets.rappelsJour.map((o) => (
            <ActionRow key={o.id} opp={o} actions={[
              { label: "Ouvrir", testid: `open-${o.id}`, onClick: () => navigate(`/commercial/opportunites/${o.id}`) },
              { label: "Appeler", testid: `call-${o.id}`, href: o.contact_telephone ? `tel:${o.contact_telephone}` : null },
            ]} badge={{ label: "Aujourd'hui", color: "bg-brand-moss/10 text-brand-moss border-brand-moss/25" }}
            />
          ))}
        </Section>

        {/* Rappels en retard */}
        {buckets.rappelsEnRetard.length > 0 && (
          <Section
            title="Rappels en retard"
            Icon={AlertCircle}
            count={buckets.rappelsEnRetard.length}
            testid="section-rappels-retard"
          >
            {buckets.rappelsEnRetard.slice(0, 10).map((o) => (
              <ActionRow key={o.id} opp={o} actions={[
                { label: "Ouvrir", testid: `open-${o.id}`, onClick: () => navigate(`/commercial/opportunites/${o.id}`) },
                { label: "Planifier une relance", testid: `relance-${o.id}`, onClick: () => navigate(`/commercial/opportunites/${o.id}`) },
              ]} badge={{ label: fmtDateTime(o.date_prochaine_action), color: "bg-[#F8F3F0] text-[#7A4B3A] border-[#E9DED8]" }}
              />
            ))}
          </Section>
        )}

        {/* RDV du jour */}
        <Section
          title="Rendez-vous du jour"
          Icon={Calendar}
          count={buckets.rdvJour.length}
          emptyText="Aucun rendez-vous aujourd'hui."
          testid="section-rdv-jour"
        >
          {buckets.rdvJour.map((a) => (
            <ApptRow key={a.id} appt={a} opps={opps} onConfirm={() => confirmAppt(a.id)} onOpen={() => navigate(`/commercial/opportunites/${a.opportunity_id}`)} />
          ))}
        </Section>

        {/* RDV à confirmer */}
        <Section
          title="Rendez-vous à confirmer"
          Icon={CalendarClock}
          count={buckets.rdvAConfirmer.length}
          emptyText="Tous les prochains RDV sont confirmés."
          testid="section-rdv-a-confirmer"
        >
          {buckets.rdvAConfirmer.slice(0, 10).map((a) => (
            <ApptRow key={a.id} appt={a} opps={opps} onConfirm={() => confirmAppt(a.id)} onOpen={() => navigate(`/commercial/opportunites/${a.opportunity_id}`)} />
          ))}
        </Section>

        {/* Opportunités sans prochaine action */}
        <Section
          title="Opportunités sans prochaine action"
          Icon={Sparkles}
          count={buckets.sansAction.length}
          emptyText="Chaque opportunité ouverte a une prochaine action planifiée."
          testid="section-sans-action"
        >
          {buckets.sansAction.slice(0, 10).map((o) => (
            <ActionRow key={o.id} opp={o} actions={[
              { label: "Planifier une relance", testid: `plan-${o.id}`, onClick: () => navigate(`/commercial/opportunites/${o.id}`) },
            ]} />
          ))}
        </Section>

        {/* Fiches incomplètes */}
        <Section
          title="Fiches incomplètes"
          Icon={FileCheck2}
          count={buckets.incompletes.length}
          emptyText="Toutes les fiches sont complètes."
          testid="section-incompletes"
        >
          {buckets.incompletes.slice(0, 10).map((o) => (
            <ActionRow key={o.id} opp={o} actions={[
              { label: "Compléter la fiche", testid: `complete-${o.id}`, onClick: () => navigate(`/commercial/opportunites/${o.id}`) },
            ]} badge={{ label: `${missingFields(o).length} champs manquants`, color: "bg-[#FAF8F1] text-[#6B5B33] border-[#E9E2CF]" }}
            />
          ))}
        </Section>

        {/* Attribution manquante */}
        {buckets.sansAttribution.length > 0 && (
          <Section
            title="Attribution manquante"
            Icon={AlertCircle}
            count={buckets.sansAttribution.length}
            testid="section-sans-attribution"
          >
            {buckets.sansAttribution.slice(0, 10).map((o) => (
              <ActionRow key={o.id} opp={o} actions={[
                { label: "Attribuer", testid: `assign-${o.id}`, onClick: () => navigate(`/commercial/opportunites/${o.id}`) },
              ]} badge={{ label: !o.teleprospectrice ? "Sans téléprospectrice" : "Sans commercial", color: "bg-[#F8F3F0] text-[#7A4B3A] border-[#E9DED8]" }}
              />
            ))}
          </Section>
        )}

        {/* Opportunités récentes */}
        <Section
          title="Opportunités récemment créées"
          Icon={Target}
          count={buckets.recentes.length}
          emptyText="Aucune opportunité pour l'instant."
          testid="section-recentes"
        >
          {buckets.recentes.map((o) => (
            <ActionRow key={o.id} opp={o} actions={[
              { label: "Ouvrir", testid: `open-${o.id}`, onClick: () => navigate(`/commercial/opportunites/${o.id}`) },
            ]} />
          ))}
        </Section>

        {/* Chiffres clés en bas de page */}
        {kpis && (
          <div className="pt-6">
            <div className="eyebrow mb-3">Chiffres clés</div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <MiniKpi label="Opportunités ouvertes" value={kpis.opportunites_ouvertes ?? 0} Icon={Target} />
              <MiniKpi label="Contrats signés" value={kpis.contrats_signes ?? 0} Icon={FileText} />
              <MiniKpi label="CA signé" value={fmtEur(kpis.ca_signe ?? 0)} Icon={TrendingUp} />
              <MiniKpi label="Commissions à payer" value={fmtEur(kpis.commissions_a_payer ?? 0)} Icon={Wallet} />
            </div>
          </div>
        )}
      </div>
    </div>
  );

  async function confirmAppt(id) {
    try { await api.patch(`/appointments/${id}`, { status: "confirme" }); toast.success("RDV confirmé"); load(); }
    catch { toast.error("Erreur"); }
  }
}

function missingFields(o) {
  const missing = [];
  if (!o.contact_nom) missing.push("contact");
  if (!o.contact_telephone) missing.push("téléphone");
  if (!o.besoin) missing.push("besoin");
  if (!o.montant_estime) missing.push("montant estimé");
  if (!o.prochaine_action) missing.push("prochaine action");
  return missing;
}

function Section({ title, Icon, count, emptyText, testid, children }) {
  const arr = Array.isArray(children) ? children : [children].filter(Boolean);
  const has = arr && arr.length > 0;
  if (!has && !emptyText) return null;
  return (
    <section className="rounded-lg border border-brand-line bg-white overflow-hidden" data-testid={testid}>
      <div className="px-4 lg:px-5 py-3 border-b border-brand-line flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <Icon className="h-4 w-4 text-brand-moss" strokeWidth={1.5} />
          <h2 className="font-display text-base text-brand-ink">{title}</h2>
        </div>
        <span className="text-[11px] font-mono text-brand-subtle">{count}</span>
      </div>
      <div className="divide-y divide-brand-line">
        {has ? children : <div className="px-4 lg:px-5 py-5 text-sm text-brand-subtle">{emptyText}</div>}
      </div>
    </section>
  );
}

function ActionRow({ opp, actions = [], badge }) {
  const s = stageById(opp.stage);
  return (
    <div className="px-4 lg:px-5 py-3 flex items-center gap-3 flex-wrap sm:flex-nowrap hover:bg-brand-paper transition-colors">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <Link to={`/commercial/opportunites/${opp.id}`} className="text-sm font-medium text-brand-ink hover:underline truncate">{opp.entreprise_name || "—"}</Link>
          <span className={`text-[10px] font-medium px-2 py-0.5 rounded border ${s.color}`}>{s.short}</span>
          {badge && <span className={`text-[10px] font-medium px-2 py-0.5 rounded border ${badge.color}`}>{badge.label}</span>}
        </div>
        <div className="mt-0.5 text-[11px] text-brand-subtle truncate">
          {opp.contact_nom || "—"} · {opp.contact_telephone || "sans téléphone"}{opp.teleprospectrice ? ` · ${opp.teleprospectrice}` : ""}
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        {actions.map((a, i) => (
          a.href
            ? <a key={i} href={a.href} data-testid={a.testid} className="inline-flex items-center gap-1 h-8 px-3 rounded-md border border-brand-line bg-white text-xs text-brand-ink hover:border-brand-ink/30">{a.label}<ChevronRight className="h-3 w-3" /></a>
            : <button key={i} onClick={a.onClick} data-testid={a.testid} className={`inline-flex items-center gap-1 h-8 px-3 rounded-md text-xs ${i === 0 ? "bg-brand-ink text-white hover:bg-brand-moss" : "border border-brand-line bg-white text-brand-ink hover:border-brand-ink/30"}`}>{a.label}<ChevronRight className="h-3 w-3" /></button>
        ))}
      </div>
    </div>
  );
}

function ApptRow({ appt, opps, onConfirm, onOpen }) {
  const opp = opps.find((o) => o.id === appt.opportunity_id);
  return (
    <div className="px-4 lg:px-5 py-3 flex items-center gap-3 flex-wrap sm:flex-nowrap hover:bg-brand-paper transition-colors">
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-brand-ink truncate">{fmtDateTime(appt.scheduled_at)} · {opp?.entreprise_name || appt.interlocuteur_nom || "—"}</div>
        <div className="mt-0.5 text-[11px] text-brand-subtle truncate">{appt.besoin || "Pas de besoin renseigné"} · {appt.commercial || "sans commercial"}</div>
      </div>
      <div className="flex items-center gap-1.5">
        {appt.status === "programme" && (
          <button onClick={onConfirm} data-testid={`confirm-${appt.id}`} className="inline-flex items-center h-8 px-3 rounded-md bg-brand-moss text-white text-xs hover:bg-brand-ink">Confirmer</button>
        )}
        <button onClick={onOpen} data-testid={`open-appt-${appt.id}`} className="inline-flex items-center h-8 px-3 rounded-md border border-brand-line bg-white text-xs text-brand-ink hover:border-brand-ink/30">Ouvrir</button>
      </div>
    </div>
  );
}

function MiniKpi({ label, value, Icon }) {
  return (
    <div className="rounded-lg border border-brand-line bg-white p-3">
      <div className="flex items-center justify-between">
        <div className="eyebrow">{label}</div>
        <Icon className="h-3.5 w-3.5 text-brand-moss" strokeWidth={1.5} />
      </div>
      <div className="mt-2 font-display text-xl text-brand-ink">{value}</div>
    </div>
  );
}
